/**
 * <ctr-video> — click-to-play video for the catering "video" section.
 *
 * The section's frame is landscape (1240x694) but the video is story-style
 * portrait, so playing it in place crops it. Clicking the poster therefore
 * opens it in a native <dialog> at every width, mobile included: inside the
 * dialog it is bounded by 90vw/90vh with object-fit: contain, so it always
 * shows whole, centered, at its own aspect ratio. Body scroll is locked while
 * the dialog is open. Client req 2026-09-08: never crop the video.
 *
 * Mobile does none of that: the video is rendered inline in the page from the
 * start, with no poster to click (client req 2026-09-10), so <ctr-video> is
 * display:none there and only sizeInlineVideos() below runs.
 *
 * Kept as its own element/file rather than reusing about-video.js, which swaps
 * the video into the poster's frame in place — the behaviour this section
 * specifically must not have. about-intro/catering-hero/franchise-video still
 * use <about-video>, untouched.
 */
class CateringVideo extends HTMLElement {
  connectedCallback() {
    this.media = this.querySelector('[data-cv-media]');
    this.playBtn = this.querySelector('[data-cv-play]');
    this.template = this.querySelector('[data-cv-template]');
    this.dialog = this.querySelector('[data-cv-dialog]');
    this.dialogMedia = this.querySelector('[data-cv-dialog-media]');
    this.closeBtn = this.querySelector('[data-cv-close]');
    if (!this.media || !this.playBtn || !this.template) return;

    this.playBtn.addEventListener('click', this.#play);
    this.closeBtn?.addEventListener('click', this.#closeDialog);
    this.dialog?.addEventListener('click', this.#onDialogClick);
    this.dialog?.addEventListener('close', this.#onDialogClose);
  }

  disconnectedCallback() {
    this.playBtn?.removeEventListener('click', this.#play);
    this.closeBtn?.removeEventListener('click', this.#closeDialog);
    this.dialog?.removeEventListener('click', this.#onDialogClick);
    this.dialog?.removeEventListener('close', this.#onDialogClose);
    // In case the element is removed from the DOM while the dialog is open.
    if (this.dialog?.open) this.#unlockScroll();
  }

  #play = () => {
    // Inline is only a fallback for a section rendered without the dialog.
    if (this.dialog) {
      this.#openDialog();
    } else {
      this.#playInline();
    }
  };

  #playInline() {
    const posterHtml = this.media.innerHTML;
    this.media.replaceChildren(this.template.content.cloneNode(true));
    this.setAttribute('playing', '');
    this.#startVideo(this.media, () => {
      // Playback was blocked (autoplay policy, network error, ...) — restore
      // the poster + play button instead of leaving a stuck, frozen video.
      this.removeAttribute('playing');
      this.media.innerHTML = posterHtml;
    });
  }

  #openDialog() {
    this.dialogMedia.replaceChildren(this.template.content.cloneNode(true));
    this.dialog.showModal();
    this.#lockScroll();
    this.#startVideo(this.dialogMedia);
  }

  #closeDialog = () => {
    this.dialog.close();
  };

  // Native <dialog> fires 'close' for both the close button and Escape, so
  // tear-down lives in one place.
  #onDialogClose = () => {
    this.dialogMedia.replaceChildren();
    this.#unlockScroll();
  };

  // Clicking the backdrop area (the <dialog> element itself, not its
  // children) closes it — Escape already closes natively.
  #onDialogClick = (event) => {
    if (event.target === this.dialog) this.#closeDialog();
  };

  #lockScroll() {
    document.documentElement.style.overflow = 'hidden';
  }

  #unlockScroll() {
    document.documentElement.style.overflow = '';
  }

  #startVideo(root, onBlocked) {
    const video = root.querySelector('video');
    if (!video) return;
    video.setAttribute('controls', '');
    // Dynamically inserted <video>/<source> pairs don't always start
    // buffering on their own in every browser — load() makes sure the
    // element has actually picked up the source before we ask it to play.
    video.load();
    const result = video.play();
    if (result && typeof result.catch === 'function') {
      result.catch(() => onBlocked?.());
    }
  }
}

if (!customElements.get('ctr-video')) {
  customElements.define('ctr-video', CateringVideo);
}

/*
 * The inline (mobile) video sits in a box shaped by --ctr-inline-ratio, which
 * the section can only guess from the embed type. Once the browser knows the
 * real dimensions, use those — so a video the client swaps later still shows
 * whole, with no letterboxing above and below it.
 */
function sizeInlineVideos() {
  document.querySelectorAll('[data-cv-inline] video').forEach((video) => {
    const apply = () => {
      if (!video.videoWidth || !video.videoHeight) return;
      video.closest('[data-cv-inline]').style.setProperty(
        '--ctr-inline-ratio',
        `${video.videoWidth} / ${video.videoHeight}`
      );
    };
    // Metadata can already be there when this runs (a cached video).
    if (video.readyState >= 1) apply();
    video.addEventListener('loadedmetadata', apply, { once: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', sizeInlineVideos, { once: true });
} else {
  sizeInlineVideos();
}

// The theme editor re-renders the whole section on every setting change.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target?.querySelector('[data-cv-inline]')) sizeInlineVideos();
});
